import { createFileRoute, Link } from "@tanstack/react-router";
import { Check } from "lucide-react";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/signup/")({
  head: () => ({
    meta: [
      { title: "Sign up | LetsListBusiness" },
      { name: "description", content: "Create a free LetsListBusiness account to write reviews, save favorites and discover the best local businesses." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: SITE_URL + "/signup/" }],
  }),
  component: SignupPage,
});

function SignupPage() {
  return (
    <section className="container mx-auto grid gap-0 px-4 py-10 lg:grid-cols-2 lg:gap-10">
      {/* Left — form */}
      <div className="order-2 flex items-center lg:order-1">
        <div className="mx-auto w-full max-w-md">
          <h1 className="font-display text-4xl font-bold">Join LetsListBusiness</h1>
          <p className="mt-2 text-sm text-muted-foreground">Free forever. No credit card.</p>

          <form className="mt-8 space-y-4">
            <Field label="Name"><input className="field" placeholder="Your name" /></Field>
            <Field label="Email"><input className="field" type="email" placeholder="you@example.com" /></Field>
            <Field label="Password"><input className="field" type="password" placeholder="At least 8 characters" /></Field>
            <button type="button" className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground">Create account</button>
          </form>

          <p className="mt-3 text-center text-[11px] text-muted-foreground">
            By signing up you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
          </p>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Already a member? <Link to="/login" className="font-semibold text-primary underline">Log in</Link>
          </p>
        </div>
      </div>

      {/* Right — pitch panel (warm gradient) */}
      <aside className="order-1 hidden overflow-hidden rounded-3xl bg-gradient-warm p-10 text-accent-foreground lg:order-2 lg:block">
        <div className="flex h-full min-h-[520px] flex-col justify-between">
          <div className="text-xs font-semibold uppercase tracking-[0.3em]">What you get</div>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight">
              Your city, <em className="not-italic">your shortlist.</em>
            </h2>
            <ul className="mt-8 space-y-3">
              {[
                "Save businesses to personal lists",
                "Write verified reviews neighbors trust",
                "Get notified when new spots open nearby",
                "Early access to local guides and roundups",
              ].map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm font-medium">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-foreground text-background">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>
      <style>{`.field{width:100%;border-radius:.625rem;border:1px solid var(--border);background:var(--background);padding:.75rem .9rem;font-size:.875rem;outline:none}.field:focus{border-color:var(--ring);box-shadow:0 0 0 3px color-mix(in oklab, var(--ring) 25%, transparent)}`}</style>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
